import { useSyncExternalStore } from 'react';
import { currentPrefs, savePrefs, subscribePrefs, type Lang } from '../screens/lib/prefs.js';
import { COPY, type CopyKey, type Phrase } from './copy.js';

/**
 * Two languages, one dictionary.
 *
 * The rule the copy follows, taken from the design doc's own EN mock (§2c —
 * "Marites played a straight", "Rooftop table", "Bet 100 · Pot 420"): prose and
 * chrome become natural English, and only proper nouns survive untranslated —
 * the six game titles, the app's name, and people's handles. A venue's nickname
 * is not a proper noun and does get translated; neither is a stake tier.
 *
 * No i18n library. The dictionary is a plain object, every entry carries both
 * languages, and `satisfies` in copy.ts makes a missing `en` a type error — so
 * "all of it is translated" is checked by the compiler rather than by reading.
 */

export type { Lang };
export type { CopyKey };

/** The device's language, re-rendering everything that reads copy when it changes. */
export function useLang(): Lang {
  return useSyncExternalStore(
    subscribePrefs,
    () => currentPrefs().lang,
    () => 'tl' as const,
  );
}

export function setLang(lang: Lang): void {
  savePrefs({ ...currentPrefs(), lang });
}

export type Vars = Record<string, string | number>;

/**
 * `{name}` placeholders, filled positionally by name. Deliberately dumb: no
 * plural rules, no dates, no number formatting — chips already have `chips()`
 * and every other number in this app is a count with a word beside it.
 */
export function translate(key: CopyKey, lang: Lang, vars?: Vars): string {
  /*
   * One count rule, because there is only one language here that needs it:
   * Tagalog does not inflect a noun after a number — "1 mesa", "2 mesa" — so
   * every counted string was written once and read fine, and then said
   * "1 tables open" the moment it was English. When `n` is exactly 1 and the
   * dictionary carries a `<key>.one`, that entry wins.
   */
  const single = vars !== undefined && Number(vars.n) === 1 ? `${key}.one` : null;
  const phrase = (single !== null && single in COPY ? COPY[single as CopyKey] : COPY[key]) as Phrase;
  const raw = lang === 'en' ? phrase.en : phrase.tl;
  if (!vars) return raw;
  return raw.replace(/\{(\w+)\}/g, (whole, name: string) =>
    name in vars ? String(vars[name]) : whole,
  );
}

/**
 * What a server error actually says to this player.
 *
 * The wire carries a code AND a sentence, and the sentence is the specific one:
 * `illegal_action` covers a bad purchase, an unknown chip pack and "you are
 * still seated at another table", so translating by code alone flattens all
 * three into one useless line. Taglish therefore reads the server verbatim —
 * which is what shipped before there was a dictionary — and English falls back
 * to the per-code copy, which is less specific and knowingly so.
 *
 * Named and tested rather than inlined in the toast, because it regressed
 * exactly once already and the failure is silent: the toast still appears, it
 * just stops telling you what to do.
 */
export function errorText(code: string | undefined, serverMessage: string, lang: Lang): string {
  if (lang === 'tl' || code === undefined) return serverMessage;
  const key = `ui.err.${code}`;
  return key in COPY ? translate(key as CopyKey, lang) : serverMessage;
}

export type T = (key: CopyKey, vars?: Vars) => string;

/** The reader a component uses. Subscribes, so switching language re-renders. */
export function useT(): T {
  const lang = useLang();
  return (key, vars) => translate(key, lang, vars);
}
